import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { Fetch } from '../../utils/Utils.js';
import { formatDate } from '../../../utils/dateFormat.js';
import '../reproduction/NivoChartCard.css';

// One metric (rainfall) over an ordered time axis, not distinct identities —
// one hue (categorical slot 1, blue), not a color per bar.
const BAR_COLOR = '#2a78d6';

// Format "2026-01" to "Jan 2026" (or "fev. de 2026" in PT)
const formatMonthLabel = (yearMonth) => {
  const [year, month] = yearMonth.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return formatDate(date, { month: 'short', year: 'numeric' });
};

const RainfallByMonth = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);

  useEffect(() => {
    Fetch('/api/data/rain', 'GET', null, (rawData) => setData(aggregateData(rawData || [])));
  }, []);

  const aggregateData = (rawData) => {
    const values = [];
    for (let i = 0; i < rawData.length; i++) {
      const yearMonth = rawData[i].date.slice(0, 7);
      const found = values.find(value => value.month === yearMonth);
      if (found === undefined) {
        values.push({ month: yearMonth, amount: rawData[i].amount });
      } else {
        found.amount += rawData[i].amount;
      }
    }
    values.sort((a, b) => a.month.localeCompare(b.month));
    return values;
  };

  const bars = data.map(d => ({ month: d.month, label: formatMonthLabel(d.month), amount: Math.round(d.amount) }));

  let summary = null;
  if (data.length > 0) {
    const total = Math.round(data.reduce((sum, d) => sum + d.amount, 0));
    const wettest = data.reduce((max, d) => d.amount > max.amount ? d : max, data[0]);
    summary = t('chart_summary_rainfall', {
      total,
      month: formatMonthLabel(wettest.month),
      amount: Math.round(wettest.amount),
    });
  }

  return (
    <div className="Chart NivoCard RainfallByMonth">
      <h3>{t('total_rainfall_by_month')}</h3>
      {summary && <p className="nivo-card-subtitle">{summary}</p>}

      {bars.length === 0 ? (
        <p className="nivo-card-empty">{t('chart_summary_no_rainfall')}</p>
      ) : (
        <div style={{ height: 300 }}>
          <ResponsiveBar
            data={bars}
            keys={['amount']}
            indexBy="label"
            margin={{ top: 12, right: 16, bottom: 42, left: 52 }}
            valueScale={{ type: 'linear' }}
            padding={0.35}
            borderRadius={4}
            colors={BAR_COLOR}
            enableGridY={true}
            gridYValues={4}
            axisBottom={{ tickSize: 0, tickPadding: 8, tickRotation: bars.length > 6 ? -30 : 0 }}
            axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 4, legend: t('rainfall_mm'), legendPosition: 'middle', legendOffset: -44 }}
            label={(d) => `${d.value}`}
            labelSkipWidth={26}
            labelSkipHeight={16}
            labelTextColor="#ffffff"
            motionConfig="gentle"
            theme={{
              axis: { ticks: { text: { fill: '#898781', fontSize: 12 } }, legend: { text: { fill: '#52514e', fontSize: 12.5 } } },
              grid: { line: { stroke: '#e1e0d9', strokeWidth: 1 } },
              labels: { text: { fontWeight: 700, fontSize: 12 } },
            }}
            tooltip={({ data: d }) => (
              <div className="nivo-tooltip">
                <span className="nivo-tooltip-swatch" style={{ background: BAR_COLOR }} />
                <strong>{d.label}</strong>: <span className="nivo-tooltip-value">{d.amount}mm</span>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default RainfallByMonth;
